import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import Button from '../common/Button';
import { Modal, ModalContent } from '../common';
import { Heading, Text, Typography } from '../common/Typography';
import { Card } from '../common/Card';
import { getSessionDetails } from '../../services/sessionManagementService';
import CancelSignupModal from './CancelSignupModal';
import ClubLogo from '../clubs/ClubLogo';
import { supabase } from '../../utils/supabase';
import { isHostSelectSignup } from '../../utils/signupSelection';
import { formatSessionTimeRange } from '../../utils/homeSessionUtils';

const SessionDetailsModal = ({ 
  sessionId, 
  isOpen, 
  onClose, 
  onSignup, 
  onCancelSignup, 
  currentUser, 
  userSignupStatus: propUserSignupStatus 
}) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [userSignupStatus, setUserSignupStatus] = useState(null);

  useEffect(() => {
    if (isOpen && sessionId) {
      fetchSessionDetails();
      if (currentUser) {
        fetchUserSignupStatus();
      }
    }
  }, [isOpen, sessionId, currentUser]);

  const fetchUserSignupStatus = async () => {
    if (!currentUser?.id || !sessionId) return;
    
    try {
      const { data, error } = await supabase
        .from('session_signups')
        .select('*')
        .eq('session_id', sessionId)
        .eq('player_id', currentUser.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error fetching user signup status:', error);
      }
      
      setUserSignupStatus(data);
    } catch (err) {
      console.error('Error fetching user signup status:', err);
    }
  };

  const fetchSessionDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const sessionData = await getSessionDetails(sessionId);
      setSession(sessionData);
    } catch (err) {
      console.error('Error fetching session details:', err);
      setError('Failed to load session details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const datePart = String(dateString).split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    if (!year || !month || !day) return '';
    return format(new Date(year, month - 1, day), 'EEEE, MMMM d, yyyy');
  };

  const getSignupButtonText = () => {
    if (!userSignupStatus) return 'Sign Up';
    
    switch (userSignupStatus.signup_status) {
      case 'confirmed':
        return isHostSelectSignup(session) ? 'Leave list' : 'Cancel Signup';
      case 'waitlist':
        if (isHostSelectSignup(session)) {
          return viewerWaitlistRank > 0 ? `Leave list (#${viewerWaitlistRank})` : 'Leave list';
        }
        return viewerWaitlistRank > 0 ? `Cancel Waitlist (#${viewerWaitlistRank})` : 'Cancel Waitlist';
      case 'cancelled':
        return 'Sign Up';
      default:
        return 'Sign Up';
    }
  };

  const handleSignupAction = async () => {
    if (!userSignupStatus || userSignupStatus.signup_status === 'cancelled' || !userSignupStatus.signup_status) {
      // Sign up action
      setActionLoading(true);
      try {
        await onSignup(sessionId);
        // Refresh both session details and user signup status
        await fetchSessionDetails();
        await fetchUserSignupStatus();
      } catch (err) {
        console.error('Error signing up:', err);
        setError('Failed to sign up for session');
      } finally {
        setActionLoading(false);
      }
    } else {
      // Show confirmation modal for cancel signup
      setShowCancelConfirm(true);
    }
  };

  const handleConfirmCancel = async () => {
    setActionLoading(true);
    try {
      await onCancelSignup(sessionId);
      // Refresh both session details and user signup status
      await fetchSessionDetails();
      await fetchUserSignupStatus();
      setShowCancelConfirm(false);
    } catch (err) {
      console.error('Error canceling signup:', err);
      setError('Failed to cancel signup');
    } finally {
      setActionLoading(false);
    }
  };

  const isSignedUp = userSignupStatus?.signup_status === 'confirmed' || userSignupStatus?.signup_status === 'waitlist';
  const confirmedCount = session?.current_players ?? session?.confirmed_players?.length ?? 0;
  const maxPlayers = session?.max_players || 16;
  const fillPercent = maxPlayers > 0 ? Math.min(100, Math.round((confirmedCount / maxPlayers) * 100)) : 0;
  const canSignup = Boolean(
    currentUser && session && session.session_status !== 'completed' && session.session_status !== 'archived'
  );

  const orderedWaitlist = [...(session?.waitlist_players || [])].sort(
    (a, b) => (Number(a.waitlist_position) || Number.MAX_SAFE_INTEGER) - (Number(b.waitlist_position) || Number.MAX_SAFE_INTEGER)
  );
  const viewerWaitlistRank = orderedWaitlist.findIndex((signup) => signup.player_id === currentUser?.id) + 1;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent size="large" padding={false} className="flex min-h-0 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 overflow-y-auto p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-6">
          <div className="flex-1 min-w-0">
            <Heading level={2} className="text-text-primary mb-2 break-words">
              {session?.name || session?.club_session_setup?.session_name || 'Session Details'}
            </Heading>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-text-secondary">
              <div className="flex items-center">
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {session && formatDate(session.session_date)}
              </div>
              <div className="flex items-center">
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {session && formatSessionTimeRange(session.start_time || session.time, session.end_time, { compact: true })}
              </div>
              {session?.clubs && (
                <div className="flex items-center gap-2">
                  {session.clubs.logo_image_url && (
                    <ClubLogo
                      logoImageUrl={session.clubs.logo_image_url}
                      clubName={session.clubs.name}
                      size="sm"
                      className="shrink-0"
                    />
                  )}
                  {!session.clubs.logo_image_url && (
                    <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                  <span>{session.clubs.name}</span>
                </div>
              )}
            </div>
          </div>
          <Button
            onClick={onClose}
            variant="outline"
            size="small"
            className="shrink-0"
          >
            ✕
          </Button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <Typography variant="body" className="text-text-secondary">Loading session details...</Typography>
          </div>
        )}

        {/* Error State */}
        {error && (
          <Card variant="danger" size="medium" className="mb-4">
            <Typography variant="body" className="text-text-secondary">{error}</Typography>
          </Card>
        )}

        {/* Session Content */}
        {!loading && session && (
          <div className="space-y-6">
            {/* Session Description */}
            {(session.description || session.club_session_setup?.description) && (
              <Card variant="default" size="medium">
                <Heading level={4} className="text-text-primary mb-2">About this session</Heading>
                <Text className="text-text-primary">
                  {session.description || session.club_session_setup?.description}
                </Text>
              </Card>
            )}

            <div className="rounded-xl border border-border bg-surface-subtle px-4 py-3">
              <div className="mb-2 flex items-baseline justify-between gap-3">
                <span className="text-xs font-medium text-text-secondary">Confirmed</span>
                <span className="text-sm font-semibold tabular-nums text-text-primary">
                  {confirmedCount} / {maxPlayers}
                </span>
              </div>
              <div
                className="h-2 overflow-hidden rounded-full bg-border-light"
                role="progressbar"
                aria-label="Confirmed spots filled"
                aria-valuemin={0}
                aria-valuemax={maxPlayers}
                aria-valuenow={confirmedCount}
              >
                <div
                  className="h-full rounded-full bg-primary transition-[width]"
                  style={{ width: `${fillPercent}%` }}
                />
              </div>
              {isHostSelectSignup(session) ? (
                <Typography variant="caption" className="mt-2 block text-text-secondary">
                  Signing up adds you to the waitlist. The host then chooses who is confirmed for the session.
                </Typography>
              ) : null}
            </div>

            {/* Signup Deadline */}
            {session.signup_deadline_hours && (
              <div className="bg-surface-subtle border border-border rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-text-secondary mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <Typography variant="body" className="text-text-primary font-medium">
                    Signup closes {session.signup_deadline_hours} hours before the session
                  </Typography>
                </div>
              </div>
            )}

            {/* Confirmed Players */}
            <div className="bg-surface border border-border rounded-lg p-4">
              <Heading level={4} className="text-text-primary mb-3 flex items-center">
                <svg className="h-5 w-5 mr-2 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Confirmed Players ({session.confirmed_players?.length || 0})
              </Heading>

              {session.confirmed_players && session.confirmed_players.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {session.confirmed_players.map((signup) => (
                    <div key={signup.id} className="flex items-center p-3 bg-surface-subtle rounded-lg border border-border">
                      <div className="flex-shrink-0 h-8 w-8 bg-primary rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-text-inverse">
                          {signup.players?.first_name?.[0]}{signup.players?.last_name?.[0]}
                        </span>
                      </div>
                      <div className="ml-3">
                        <Typography variant="body" className="text-text-primary font-medium">
                          {signup.players?.first_name} {signup.players?.last_name}
                        </Typography>
                        <Typography variant="muted" className="text-xs">
                          Joined {format(new Date(signup.signup_date), 'MMM dd')}
                        </Typography>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Typography variant="muted" className="text-center py-4">No confirmed players yet</Typography>
              )}
            </div>

            {/* Waitlist Players */}
            {session.waitlist_enabled && (
              <div className="bg-surface border border-border rounded-lg p-4">
                <Heading level={4} className="text-text-primary mb-3 flex items-center">
                  <svg className="h-5 w-5 mr-2 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Waitlist ({session.waitlist_players?.length || 0})
                </Heading>

                {orderedWaitlist.length > 0 ? (
                  <div className="space-y-2">
                    {orderedWaitlist.map((signup, index) => (
                        <div key={signup.id} className="flex items-center p-3 bg-surface-subtle rounded-lg border border-border">
                          <div className="flex-shrink-0 h-8 w-8 bg-warning rounded-full flex items-center justify-center">
                            <span className="text-sm font-bold text-text-inverse">
                              {index + 1}
                            </span>
                          </div>
                          <div className="ml-3 flex-1">
                            <Typography variant="body" className="text-text-primary font-medium">
                              {signup.players?.first_name} {signup.players?.last_name}
                            </Typography>
                            <Typography variant="muted" className="text-xs">
                              {isHostSelectSignup(session) ? 'Signed up' : 'Joined waitlist'} {format(new Date(signup.signup_date), 'MMM dd')}
                            </Typography>
                          </div>
                        </div>
                    ))}
                  </div>
                ) : (
                  <Typography variant="muted" className="text-center py-4">No players on waitlist</Typography>
                )}
              </div>
            )}

            {/* Cancelled Players */}
            {session.cancelled_players && session.cancelled_players.length > 0 && (
              <div className="bg-surface border border-border rounded-lg p-4">
                <Heading level={4} className="text-text-primary mb-3 flex items-center">
                  <svg className="h-5 w-5 mr-2 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancelled Players ({session.cancelled_players.length})
                </Heading>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {session.cancelled_players.map((signup) => (
                    <div key={signup.id} className="flex items-center p-3 bg-surface-subtle rounded-lg border border-border opacity-75">
                      <div className="flex-shrink-0 h-8 w-8 bg-text-muted rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-text-inverse">
                          {signup.players?.first_name?.[0]}{signup.players?.last_name?.[0]}
                        </span>
                      </div>
                      <div className="ml-3">
                        <Typography variant="body" className="text-text-secondary font-medium">
                          {signup.players?.first_name} {signup.players?.last_name}
                        </Typography>
                        <Typography variant="muted" className="text-xs">
                          Cancelled
                        </Typography>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Your Status */}
            {userSignupStatus && userSignupStatus.signup_status !== 'not_signed_up' && (
              <div className="bg-surface-subtle border border-border rounded-lg p-4">
                <Heading level={4} className="text-text-primary mb-2">Your Status</Heading>
                <div className="flex items-center">
                  {userSignupStatus.signup_status === 'confirmed' ? (
                    <>
                      <svg className="h-5 w-5 text-success mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <Typography variant="body" className="text-text-primary font-medium">
                        {isHostSelectSignup(session) ? 'The host has confirmed you for this session.' : "You're confirmed for this session!"}
                      </Typography>
                    </>
                  ) : userSignupStatus.signup_status === 'waitlist' ? (
                    <>
                      <svg className="h-5 w-5 text-warning mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <Typography variant="body" className="text-text-primary font-medium">
                          {isHostSelectSignup(session)
                            ? `You're #${viewerWaitlistRank || orderedWaitlist.length} on the list`
                            : `You're #${viewerWaitlistRank || orderedWaitlist.length} on the waitlist`}
                        </Typography>
                        {isHostSelectSignup(session) && userSignupStatus.signup_date ? (
                          <Typography variant="muted" className="text-xs">
                            Signed up {format(new Date(userSignupStatus.signup_date), 'MMM d, h:mm a')}
                          </Typography>
                        ) : null}
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            )}

          </div>
        )}
          </div>
          <div className="shrink-0 border-t border-border bg-surface px-5 py-3">
            <div className="flex gap-3">
              <Button onClick={onClose} variant="outline" className="flex-1 sm:flex-none">
                Close
              </Button>
              {canSignup ? (
                <Button
                  onClick={handleSignupAction}
                  variant={isSignedUp ? 'danger' : 'success'}
                  disabled={actionLoading}
                  className="flex-1 sm:flex-none"
                >
                  {actionLoading ? 'Loading...' : getSignupButtonText()}
                </Button>
              ) : null}
            </div>
          </div>
        </ModalContent>
      </Modal>

      <CancelSignupModal
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={handleConfirmCancel}
        sessionName={session?.name || 'this session'}
        userSignupStatus={userSignupStatus}
        isLoading={actionLoading}
      />
    </>
  );
};

export default SessionDetailsModal;
